/*
  | ===================
  |   Utils Sungnikah
  | ===================
  | Author  : Sungnikah Corporation's.
  | Date    : 19 Oktober 2022
  | Develop : Dimas Prasetyo (Frontend Dev).
*/

import { format } from 'date-fns';
import { id } from 'date-fns/locale';

/**
 *
 * @param {String} paths
 * @returns string url -> Unique ID
*/

export const getLastUrl = (paths) => {
  if (paths !== undefined) {
    return paths.pathname.substring(paths.pathname.lastIndexOf('/') + 1);
  }
  return paths
}

export const getSubUrl = (paths, prefix) => {
  if (paths !== undefined) {
    if (prefix === 'edit') {
      return paths.pathname.substring(paths.pathname.lastIndexOf('/edit/') + 1).split(['/'])[1];
    }
  }
  return paths
}

export const getThemeCoverUrl = (theme) => {
  if (!theme) return '';

  const storedPath = theme.cover_themes_url || theme.full_path_cover_themes || '';
  if (/^https?:\/\//i.test(storedPath)) return storedPath;

  const storageHost = (process.env.REACT_APP_HOST_MULTER || '').replace(/\/$/, '');
  const normalizedPath = storedPath.replace(/\\/g, '/').replace(/^\.\//, '');
  const storageIndex = normalizedPath.indexOf('storages/');

  if (storageIndex >= 0) {
    return `${storageHost}/${normalizedPath.slice(storageIndex + 'storages/'.length)}`;
  }

  return theme.themes_id && theme.cover_themes
    ? `${storageHost}/themes-product/${encodeURIComponent(theme.themes_id)}/${encodeURIComponent(theme.cover_themes)}`
    : '';
}

export const getThemeCoverDetailUrl = (theme) => {
  if (!theme) return '';

  const storedPath = theme.cover_details_themes_url || theme.full_path_cover_details_themes || '';
  if (/^https?:\/\//i.test(storedPath)) return storedPath;

  const storageHost = (process.env.REACT_APP_HOST_MULTER || '').replace(/\/$/, '');
  const normalizedPath = storedPath.replace(/\\/g, '/').replace(/^\.\//, '');
  const storageIndex = normalizedPath.indexOf('storages/');

  if (storageIndex >= 0) {
    return `${storageHost}/${normalizedPath.slice(storageIndex + 'storages/'.length)}`;
  }

  return theme.themes_id && theme.cover_details_themes
    ? `${storageHost}/themes-product/${encodeURIComponent(theme.themes_id)}/${encodeURIComponent(theme.cover_details_themes)}`
    : '';
}

export const getDemoCoverUrl = (demo) => {
  if (!demo) return '';

  const storedPath = demo.cover_demos_url || demo.full_path_cover_demos || '';
  if (/^https?:\/\//i.test(storedPath)) return storedPath;

  const storageHost = (process.env.REACT_APP_HOST_MULTER || '').replace(/\/$/, '');
  const normalizedPath = storedPath.replace(/\\/g, '/').replace(/^\.\//, '');
  const storageIndex = normalizedPath.indexOf('storages/');

  if (storageIndex >= 0) {
    return `${storageHost}/${normalizedPath.slice(storageIndex + 'storages/'.length)}`;
  }

  return demo.demos_id && demo.cover_demos
    ? `${storageHost}/demos/${encodeURIComponent(demo.demos_id)}/${encodeURIComponent(demo.cover_demos)}`
    : '';
}


/**
 * Resolves the article cover URL the same way getThemeCoverUrl does.
 * Priority: fully-qualified stored URL -> path containing `storages/` -> rebuild from article slug + filename.
 *
 * @param {object} article - article object (article_cover, article_slug/article_name from the blog API).
 * @returns {string} resolved cover URL or empty string.
 */
export const getArticleCoverUrl = (article) => {
  if (!article) return '';

  const storedPath = article.article_cover || article.full_path_article_cover || '';
  if (/^https?:\/\//i.test(storedPath)) return storedPath;

  const storageHost = (process.env.REACT_APP_HOST_MULTER || '').replace(/\/$/, '');
  const normalizedPath = storedPath.replace(/\\/g, '/').replace(/^\.\//, '');
  const storageIndex = normalizedPath.indexOf('storages/');

  if (storageIndex >= 0) {
    return `${storageHost}/${normalizedPath.slice(storageIndex + 'storages/'.length)}`;
  }

  if (!article.article_cover) return '';

  const articleSlug = article.article_slug || (article.article_name ? removeSpaceCharacter(article.article_name.toLowerCase()) : '');

  return articleSlug
    ? `${storageHost}/blog/${encodeURIComponent(articleSlug)}/${encodeURIComponent(article.article_cover)}`
    : `${storageHost}/blog/${encodeURIComponent(article.article_cover)}`;
}

/**
 * Resolves the payment proof URL the same way getThemeCoverUrl does.
 * Priority: fully-qualified stored URL -> path containing `storages/` -> rebuild
 * from the legacy `payment-conf` folder + filename.
 *
 * @param {object} payment - payment confirmation object (upload_proof_conf, full_path_upload_proof_conf).
 * @returns {string} resolved proof URL or empty string.
 */
export const getPaymentProofUrl = (payment) => {
  if (!payment) return '';

  const storedPath = payment.full_path_upload_proof_conf || '';
  if (/^https?:\/\//i.test(storedPath)) return storedPath;

  const storageHost = (process.env.REACT_APP_HOST_MULTER || '').replace(/\/$/, '');
  const normalizedPath = storedPath.replace(/\\/g, '/').replace(/^\.\//, '');
  const storageIndex = normalizedPath.indexOf('storages/');

  if (storageIndex >= 0) {
    return `${storageHost}/${normalizedPath.slice(storageIndex + 'storages/'.length)}`;
  }

  return payment.upload_proof_conf
    ? `${storageHost}/payment-conf/${encodeURIComponent(payment.upload_proof_conf)}`
    : '';
}

/**
 *
 * @param {string} value
 * @returns Number
 */
export const removeThousandSeparator = (value) => {
  let lastIndex = 2
  if (value.toString().length > 5) {
    lastIndex = 3
  }
  return value.toString().substring(0, lastIndex)
}

/**
 *
 * @param {number} value
 * @returns string with separator (.)
 */
const numberWithCommas = (value) => {
  if (value !== undefined) {
    return value.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ".");
  }
}

export const numberFormat = (value) => {
  return numberWithCommas(value);
}

/**
 *
 * @param {string} token
 * @returns
 */
export const parseJwt = (token) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));

  return JSON.parse(jsonPayload);
};


/**
 *
 * @param {any} length
 * @returns {random string}
 */

export const randomString = (prefix = false, length) => {
  let result           = '';
  let characters       = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() *
    charactersLength));
  }
  if (prefix) {
    return prefix + result;
  }
  return result
}

/**
 *
 * @returns {string} times
 *
 */

export const getTimes = () => {
  let today = new Date();
  let times = today.getHours() + ":" + today.getMinutes() + ":" + today.getSeconds();
  return times
}

export const getDates = () => {
  let today = new Date();
  let fullDates = today.getFullYear()+'-'+(today.getMonth()+1)+'-'+today.getDate();
  return fullDates
}

/**
 *
 * @param {string} value
 * @returns string
 */
 export const removeSpaceCharacter = (value) => {
  if (value !== undefined) {
    const getChars = value.split(' ').join('-');
    return getChars
  }
  return value
}

/**
 *
 * @param {date} value
 * @returns date
 */
 export const dateIndo = (value) => {
  return format(new Date(value), 'dd MMMM yyyy', {locale: id});
}


/**
 *
 * @param {datetime} value
 * @returns datetime
 */
export const dateTimeIndo = (value) => {
  return format(new Date(value), 'dd MMMM yyyy HH:mm:ss', {locale: id});
}

/**
 *
 * @param {string} value
 * @returns string remove double quotes at string
 */
export const sanitaziString = (value) => {
  if (typeof value !== 'object' && value !== null) {
    if (typeof value == 'string') {
      return value.replace(/['"]+/g, '');
    }
    return value
  }
}

/**
 *
 * @returns {number} value
 * @returns function returning comparator search data
 *
 */
export const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

/**
 *
 * @returns {string} value
 * @returns function return string status attendance
 *
 */
export const isStatusAttendance = (is_attendance_stats) => {
  if (is_attendance_stats === '0') {
    is_attendance_stats = 'Menunggu Konfirmasi'
  } else if (is_attendance_stats === '1') {
    is_attendance_stats = 'Hadir'
  } else if (is_attendance_stats == '2') {
    is_attendance_stats = 'Tidak Hadir'
  } else {
    is_attendance_stats = 'Tentative'
  }

  return is_attendance_stats
}

/**
 *
 * @returns {string} value
 * @returns function return string status comments
 *
 */
export const isCommentStatus = (attendance_stats) => {
   if (attendance_stats == '0') {
    attendance_stats = 'Visible'
  } else if (attendance_stats == '1') {
    attendance_stats = 'Disabled'
  } else if (attendance_stats == '2') {
    attendance_stats = 'Already Responded'
  }
  return attendance_stats
}

/**
 *
 * @returns {string} value
 * @returns function return string chip variants
 *
 */
export const isChipVariants = (chip_variant) => {
  if (chip_variant == '0') {
   chip_variant = 'success'
 } else if (chip_variant == '1') {
   chip_variant = 'error'
 } else if (chip_variant == '2') {
   chip_variant = 'success'
 }
 return chip_variant
}


/**
 *
 * @returns {string} value
 * @returns function return string host uri port
 *
 */

export const generateHostUrl = () => {
  const fullUrl = window.location.origin;
  return fullUrl
}
